import React, { useState, useEffect, useRef, useCallback } from 'react';
import Map, { Marker, NavigationControl, Popup, MapRef } from 'react-map-gl/mapbox';
import { Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faMapMarkerAlt, faDollarSign, faBed, faBath, faRuler } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../../helpers/api';
import 'mapbox-gl/dist/mapbox-gl.css';
import './PropertyMapbox.css';

interface Property {
  id: string;
  address: string;
  latitude?: number;
  longitude?: number;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  status?: string;
  matchScore?: number;
  listingBrokerage?: string;
  listingAgent?: string;
  mls_number?: string;
}

interface PropertyMapboxProps {
  properties: Property[];
  selectedPropertyId?: string;
  onPropertySelect?: (property: Property) => void;
  height?: string;
  showNavigation?: boolean;
}

const PropertyMapbox: React.FC<PropertyMapboxProps> = ({
  properties,
  selectedPropertyId,
  onPropertySelect,
  height = '500px',
  showNavigation = true
}) => {
  const [mapboxToken, setMapboxToken] = useState<string>('');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [viewport, setViewport] = useState({
    longitude: -112.0740,
    latitude: 33.4484,
    zoom: 11
  });
  const mapRef = useRef<MapRef>(null);

  // Fetch Mapbox token
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const response = await axiosInstance.get('/properties/config/mapbox');
        if (response.data.mapboxToken) {
          setMapboxToken(response.data.mapboxToken);
        }
      } catch (error) {
        console.error('Failed to fetch Mapbox token:', error);
        // Fallback token if available
        const fallbackToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || 
          'pk.eyJ1IjoiaG91c2VleiIsImEiOiJjbHQyZ2dyMm0wMGxxMmxuczR5dmlpOWxtIn0.s-vyWzYxKcc2c7ObD5gvqQ';
        setMapboxToken(fallbackToken);
      }
    };
    
    fetchMapboxToken();
  }, []);

  // Fit map to property bounds
  const fitMapToBounds = useCallback(() => {
    if (!mapRef.current || properties.length === 0) return;
    
    const validProperties = properties.filter(p => p.latitude && p.longitude);
    if (validProperties.length === 0) return;
    
    if (validProperties.length === 1) {
      // Single property - center on it
      mapRef.current.flyTo({
        center: [validProperties[0].longitude!, validProperties[0].latitude!],
        zoom: 15,
        duration: 1000
      });
    } else {
      // Multiple properties - fit bounds
      const lngs = validProperties.map(p => p.longitude!);
      const lats = validProperties.map(p => p.latitude!);
      
      const bounds = [
        [Math.min(...lngs), Math.min(...lats)], // Southwest
        [Math.max(...lngs), Math.max(...lats)]  // Northeast
      ] as [[number, number], [number, number]];
      
      mapRef.current.fitBounds(bounds, {
        padding: 50,
        duration: 1000
      });
    }
  }, [properties]);

  // Fit bounds when properties change
  useEffect(() => {
    fitMapToBounds();
  }, [fitMapToBounds]);

  const handleMarkerClick = (property: Property) => {
    setSelectedProperty(property);
    if (onPropertySelect) {
      onPropertySelect(property);
    }
  };

  const getMarkerColor = (property: Property) => {
    if (property.id === selectedPropertyId) return '#e74c3c'; // Red for selected
    if (property.matchScore && property.matchScore >= 90) return '#27ae60'; // Green for perfect match
    if (property.matchScore && property.matchScore >= 70) return '#3498db'; // Blue for good match
    return '#95a5a6'; // Gray for others
  };

  if (!mapboxToken) {
    return (
      <Card style={{ height }}>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <p className="text-muted">Loading map...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="property-mapbox-container" style={{ height }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={mapboxToken}
        initialViewState={viewport}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        onMove={evt => setViewport(evt.viewState)}
      >
        {showNavigation && <NavigationControl position="top-right" />}
        
        {properties.map((property) => {
          const hasCoordinates = property.latitude && property.longitude;
          if (!hasCoordinates) return null;

          return (
            <Marker
              key={property.id}
              longitude={property.longitude!}
              latitude={property.latitude!}
              anchor="bottom"
              onClick={() => handleMarkerClick(property)}
            >
              <div 
                className="custom-marker"
                style={{
                  backgroundColor: getMarkerColor(property),
                  color: 'white',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FontAwesomeIcon icon={faHome} />
              </div>
            </Marker>
          );
        })}

        {selectedProperty && selectedProperty.latitude && selectedProperty.longitude && (
          <Popup
            longitude={selectedProperty.longitude}
            latitude={selectedProperty.latitude}
            anchor="bottom"
            onClose={() => setSelectedProperty(null)}
            closeButton={true}
            closeOnClick={false}
            offset={[0, -10]}
          >
            <div className="map-popup-content" style={{ maxWidth: '250px' }}>
              <h6 className="mb-2">${selectedProperty.price.toLocaleString()}</h6>
              <p className="mb-1 small">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-muted" />
                {selectedProperty.address}
              </p>
              
              <div className="d-flex justify-content-between small text-muted mb-2">
                <span><FontAwesomeIcon icon={faBed} className="me-1" />{selectedProperty.bedrooms} beds</span>
                <span><FontAwesomeIcon icon={faBath} className="me-1" />{selectedProperty.bathrooms} baths</span>
                <span><FontAwesomeIcon icon={faRuler} className="me-1" />{selectedProperty.square_feet.toLocaleString()} sqft</span>
              </div>

              {selectedProperty.listingBrokerage && (
                <p className="mb-1 small text-muted">
                  Listing courtesy of {selectedProperty.listingBrokerage}
                </p>
              )}

              {selectedProperty.matchScore && (
                <Badge 
                  bg={selectedProperty.matchScore >= 90 ? 'success' : 
                      selectedProperty.matchScore >= 70 ? 'primary' : 'secondary'}
                  className="w-100"
                >
                  {selectedProperty.matchScore}% Match
                </Badge>
              )}

              <Button 
                variant="primary" 
                size="sm" 
                className="w-100 mt-2"
                onClick={() => onPropertySelect && onPropertySelect(selectedProperty)}
              >
                View Details
              </Button>
            </div>
          </Popup>
        )}
      </Map>

      {/* MLS Attribution */}
      <div className="mls-attribution-overlay">
        <small>
          © {new Date().getFullYear()} Arizona Regional Multiple Listing Service, Inc.
        </small>
      </div>
    </div>
  );
};

export default PropertyMapbox;